/**
 * Sujets déjà utilisés par le gestionnaire de médias, conservés localement
 * (navigateur) par pool. Aucune donnée n'est envoyée au serveur.
 */

export const USED_OPPORTUNITIES_KEY = 'ci-used-opportunities';
export const USED_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

export type UsedOpportunities = Record<string, number>;

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function usedKey(leagueId: number, opportunityId: string): string {
  return `${leagueId}:${opportunityId}`;
}

/** Retire les entrées invalides ou plus vieilles que la rétention. */
export function pruneUsed(entries: UsedOpportunities, now = Date.now()): UsedOpportunities {
  const result: UsedOpportunities = {};
  for (const [key, time] of Object.entries(entries)) {
    if (typeof time === 'number' && Number.isFinite(time) && now - time <= USED_RETENTION_MS) {
      result[key] = time;
    }
  }
  return result;
}

export function loadUsed(now = Date.now()): UsedOpportunities {
  const raw = storage()?.getItem(USED_OPPORTUNITIES_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? pruneUsed(parsed as UsedOpportunities, now)
      : {};
  } catch {
    return {};
  }
}

export function saveUsed(entries: UsedOpportunities): void {
  try {
    storage()?.setItem(USED_OPPORTUNITIES_KEY, JSON.stringify(entries));
  } catch {
    // Stockage plein ou désactivé : le marquage reste valable pour la session.
  }
}

/** Bascule l'état « déjà utilisé » et renvoie la nouvelle liste (non mutée). */
export function toggleUsed(
  entries: UsedOpportunities,
  leagueId: number,
  opportunityId: string,
  now = Date.now()
): UsedOpportunities {
  const key = usedKey(leagueId, opportunityId);
  const next = { ...pruneUsed(entries, now) };
  if (key in next) delete next[key];
  else next[key] = now;
  return next;
}

export function isUsed(entries: UsedOpportunities, leagueId: number, opportunityId: string): boolean {
  return usedKey(leagueId, opportunityId) in entries;
}
